import React, { useState, useEffect } from 'react';
import { X, Filter } from 'lucide-react';
import { Button } from './Button';
import { Select } from './Select';
import { Input } from './Input';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';

interface FiltroModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAplicarFiltro: (filtros: FiltroUsuario) => void;
  filtroAtual?: FiltroUsuario;
}

export interface FiltroUsuario {
  status?: 'todos' | 'ativo' | 'bloqueado';
  tipo?: 'todos' | 'usuario' | 'revenda' | 'admin';
  saldoOperador?: '=' | '<' | '>';
  saldoValor?: number;
}

export const FiltroModal: React.FC<FiltroModalProps> = ({
  isOpen,
  onClose,
  onAplicarFiltro,
  filtroAtual
}) => {
  const { user } = useAuth();
  const isAdmin = user?.tipo === 'admin';

  const [status, setStatus] = useState<string>(filtroAtual?.status || 'todos');
  const [tipo, setTipo] = useState<string>(filtroAtual?.tipo || 'todos');
  const [saldoOperador, setSaldoOperador] = useState<string>(filtroAtual?.saldoOperador || '');
  const [saldoValor, setSaldoValor] = useState<string>(filtroAtual?.saldoValor?.toString() || '');

  useEffect(() => {
    if (isOpen && filtroAtual) {
      setStatus(filtroAtual.status || 'todos');
      setTipo(filtroAtual.tipo || 'todos');
      setSaldoOperador(filtroAtual.saldoOperador || '');
      setSaldoValor(filtroAtual.saldoValor?.toString() || '');
    }
  }, [isOpen, filtroAtual]);

  const handleAplicarFiltro = () => {
    const filtro: FiltroUsuario = {
      status: status as 'todos' | 'ativo' | 'bloqueado',
      // O filtro por tipo é exclusivo de administradores
      tipo: isAdmin ? (tipo as 'todos' | 'usuario' | 'revenda' | 'admin') : 'todos',
      saldoOperador: saldoOperador as '=' | '<' | '>' | undefined,
      saldoValor: saldoValor ? parseFloat(saldoValor) : undefined,
    };

    // Se operador está selecionado mas valor não, ou vice-versa, limpa ambos
    if ((filtro.saldoOperador && !filtro.saldoValor && filtro.saldoValor !== 0) || 
        (!filtro.saldoOperador && filtro.saldoValor !== undefined)) {
      filtro.saldoOperador = undefined;
      filtro.saldoValor = undefined;
    }

    onAplicarFiltro(filtro);
    onClose();
  };

  const handleLimparFiltros = () => {
    setStatus('todos');
    setTipo('todos');
    setSaldoOperador('');
    setSaldoValor('');
    onAplicarFiltro({
      status: 'todos',
      tipo: 'todos',
      saldoOperador: undefined,
      saldoValor: undefined,
    });
    onClose();
  };

  if (!isOpen) return null;

  const statusOptions = [
    { value: 'todos', label: 'Todos' },
    { value: 'ativo', label: 'Ativo' },
    { value: 'bloqueado', label: 'Bloqueado' },
  ];

  const tipoOptions = [
    { value: 'todos', label: 'Todos' },
    { value: 'usuario', label: 'Usuários' },
    { value: 'revenda', label: 'Revendas' },
    { value: 'admin', label: 'Administradores' },
  ];

  const operadorOptions = [
    { value: '', label: 'Selecione...' },
    { value: '=', label: 'Igual a (=)' },
    { value: '<', label: 'Menor que (<)' },
    { value: '>', label: 'Maior que (>)' },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
      
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-center gap-2 mb-6">
            <Filter size={20} className="text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">
              Filtrar Usuários
            </h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <Select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                options={statusOptions}
              />
            </div>

            {isAdmin && (
              <div>
                <Select
                  label="Tipo"
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                  options={tipoOptions}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filtro por Saldo
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Select
                  value={saldoOperador}
                  onChange={(e) => setSaldoOperador(e.target.value)}
                  options={operadorOptions}
                />
                <Input
                  type="number"
                  placeholder="Valor"
                  value={saldoValor}
                  onChange={(e) => setSaldoValor(e.target.value)}
                  min="0"
                  step="1"
                />
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex gap-2 justify-end">
            <Button
              onClick={handleLimparFiltros}
              className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-md"
            >
              Limpar
            </Button>
            <Button onClick={handleAplicarFiltro}>
              Aplicar Filtros
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}; 